import { createContext, useContext, useEffect } from "react";
import { SITE_URL, BRAND, EMAIL, PHONE } from "@/data/site";

export const SeoContext = createContext(null);
export const OG_IMAGE = `${SITE_URL}/og/studiowebns-og-2026.jpg`;

export const crumbsLd = (items) => ({
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: items.map((c, i) => ({ "@type": "ListItem", position: i + 1, name: c.label, item: `${SITE_URL}${c.to}` })),
});

export const faqLd = (faqs) => ({
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: faqs.map((f) => ({ "@type": "Question", name: f.q, acceptedAnswer: { "@type": "Answer", text: f.a } })),
});

export const orgLd = {
  "@context": "https://schema.org",
  "@type": "ProfessionalService",
  name: BRAND,
  url: SITE_URL,
  email: EMAIL,
  telephone: PHONE,
  areaServed: { "@type": "Country", name: "Italia" },
  priceRange: "€€",
};

function setTag(selector, create, attrs) {
  let el = document.head.querySelector(selector);
  if (!el) {
    el = document.createElement(create);
    document.head.appendChild(el);
  }
  Object.entries(attrs).forEach(([k, v]) => el.setAttribute(k, v));
}

export function buildHead({ title, description, path, jsonLd = [], noindex }) {
  const canonical = `${SITE_URL}${path}`;
  return { title, description, canonical, jsonLd: [].concat(jsonLd), noindex, ogImage: OG_IMAGE };
}

export default function Seo(props) {
  const ctx = useContext(SeoContext);
  const head = buildHead(props);
  if (ctx) Object.assign(ctx, head);
  const key = JSON.stringify(head);

  useEffect(() => {
    document.title = head.title;
    setTag('meta[name="description"]', "meta", { name: "description", content: head.description });
    setTag('meta[name="robots"]', "meta", { name: "robots", content: head.noindex ? "noindex, nofollow" : "index, follow" });
    setTag('link[rel="canonical"]', "link", { rel: "canonical", href: head.canonical });
    setTag('meta[property="og:title"]', "meta", { property: "og:title", content: head.title });
    setTag('meta[property="og:description"]', "meta", { property: "og:description", content: head.description });
    setTag('meta[property="og:url"]', "meta", { property: "og:url", content: head.canonical });
    setTag('meta[name="twitter:title"]', "meta", { name: "twitter:title", content: head.title });
    setTag('meta[name="twitter:description"]', "meta", { name: "twitter:description", content: head.description });
    document.head.querySelectorAll('script[data-ld="1"]').forEach((s) => s.remove());
    head.jsonLd.forEach((ld) => {
      const s = document.createElement("script");
      s.type = "application/ld+json";
      s.dataset.ld = "1";
      s.textContent = JSON.stringify(ld);
      document.head.appendChild(s);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  return null;
}
